import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useBlockedAccessGuard } from "../../hooks/useBlockedAccessGuard";
import { financeApi } from "../../lib/api";
import { fallbackCurrencies } from "../../lib/labels";
import { queryKeys } from "../../lib/queryKeys";
import type { Group, TransactionPage } from "../../lib/types";
import type { RecordFilters, RecordMode } from "./recordsFilters";

const initialRecordLimit = 30;
const recordLimitIncrement = 15;

function trimInfiniteTransactionCache(queryClient: QueryClient, queryKey: QueryKey) {
  queryClient.setQueryData<InfiniteData<TransactionPage, number>>(queryKey, (data) => {
    if (!data || data.pages.length <= 1) return data;
    return {
      pages: data.pages.slice(0, 1),
      pageParams: data.pageParams.slice(0, 1),
    };
  });
}

export function useRecordsQuery({
  group,
  mode,
  filters,
  search,
  asOfDate,
  onBlockedAccess,
}: {
  group: Group;
  mode: RecordMode;
  filters: RecordFilters;
  search: string;
  asOfDate: string | null;
  onBlockedAccess?: () => void;
}) {
  const queryClient = useQueryClient();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const membersQuery = useQuery({ queryKey: queryKeys.members(group.id), queryFn: () => financeApi.members(group.id) });
  const accountsQuery = useQuery({ queryKey: queryKeys.accounts(group.id), queryFn: () => financeApi.accounts(group.id) });
  const categoriesQuery = useQuery({ queryKey: queryKeys.categories(group.id), queryFn: () => financeApi.categories(group.id) });
  const tagsQuery = useQuery({ queryKey: queryKeys.tags(group.id), queryFn: () => financeApi.tags(group.id) });
  const currenciesQuery = useQuery({ queryKey: queryKeys.currencies(group.id), queryFn: () => financeApi.currencies(group.id) });
  const serverFilters = useMemo(() => ({
    type: filters.type === "linked" || filters.type === "transfer" ? ("" as const) : filters.type,
    linked: filters.type === "linked" ? true : undefined,
    logicalTransfers: filters.type === "transfer" ? true : undefined,
    categoryId: filters.categoryId,
    uncategorized: filters.uncategorized || undefined,
    userId: filters.userId,
    accountId: filters.accountId,
    tagIds: filters.tagIds,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    search: search.trim(),
  }), [filters.accountId, filters.categoryId, filters.dateFrom, filters.dateTo, filters.tagIds, filters.type, filters.uncategorized, filters.userId, search]);
  const recordsQueryKey = useMemo(() => [...queryKeys.recordsList(group.id, serverFilters), asOfDate] as const, [asOfDate, group.id, serverFilters]);
  const deletedRecordsQueryKey = useMemo(() => [...queryKeys.deletedRecordsList(group.id, serverFilters), asOfDate] as const, [asOfDate, group.id, serverFilters]);
  const recordsQuery = useInfiniteQuery({
    queryKey: recordsQueryKey,
    queryFn: ({ pageParam }) => financeApi.transactionsPage(group.id, {
      ...serverFilters,
      limit: pageParam === 0 ? initialRecordLimit : recordLimitIncrement,
      offset: pageParam,
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    enabled: mode === "active",
  });
  const deletedRecordsQuery = useInfiniteQuery({
    queryKey: deletedRecordsQueryKey,
    queryFn: ({ pageParam }) => financeApi.deletedTransactionsPage(group.id, {
      ...serverFilters,
      limit: pageParam === 0 ? initialRecordLimit : recordLimitIncrement,
      offset: pageParam,
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
    enabled: mode === "deleted",
  });
  const deletedRecordsPreviewQuery = useQuery({
    queryKey: queryKeys.deletedRecordsPreview(group.id),
    queryFn: () => financeApi.deletedTransactionsPage(group.id, { limit: 1, offset: 0 }),
  });
  const visibleRecords = useMemo(
    () => (mode === "deleted" ? deletedRecordsQuery.data?.pages || [] : recordsQuery.data?.pages || []).flatMap((page) => page.items),
    [deletedRecordsQuery.data?.pages, mode, recordsQuery.data?.pages],
  );
  const hasMoreRecords = mode === "deleted" ? deletedRecordsQuery.hasNextPage : recordsQuery.hasNextPage;
  const isFetchingMoreRecords = mode === "deleted" ? deletedRecordsQuery.isFetchingNextPage : recordsQuery.isFetchingNextPage;
  const fetchMoreRecords = mode === "deleted" ? deletedRecordsQuery.fetchNextPage : recordsQuery.fetchNextPage;

  useBlockedAccessGuard(
    [membersQuery.error, accountsQuery.error, categoriesQuery.error, tagsQuery.error, currenciesQuery.error, recordsQuery.error, deletedRecordsQuery.error, deletedRecordsPreviewQuery.error],
    onBlockedAccess,
  );
  useEffect(() => () => {
    trimInfiniteTransactionCache(queryClient, recordsQueryKey);
    trimInfiniteTransactionCache(queryClient, deletedRecordsQueryKey);
  }, [deletedRecordsQueryKey, queryClient, recordsQueryKey]);
  useEffect(() => {
    const loadMoreNode = loadMoreRef.current;
    if (!loadMoreNode || !hasMoreRecords || isFetchingMoreRecords) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void fetchMoreRecords();
    }, { rootMargin: "360px 0px" });
    observer.observe(loadMoreNode);
    return () => observer.disconnect();
  }, [fetchMoreRecords, hasMoreRecords, isFetchingMoreRecords]);

  return {
    accounts: accountsQuery.data || [],
    categories: categoriesQuery.data || [],
    tags: tagsQuery.data || [],
    currencies: currenciesQuery.data?.length ? currenciesQuery.data : fallbackCurrencies,
    members: membersQuery.data || [],
    visibleRecords,
    hasMoreRecords,
    loadMoreRef,
    deletedRecordsPreview: deletedRecordsPreviewQuery.data,
    isLoading: membersQuery.isLoading || accountsQuery.isLoading || categoriesQuery.isLoading ||
      tagsQuery.isLoading || (mode === "deleted" ? deletedRecordsQuery.isLoading : recordsQuery.isLoading),
  };
}
